import React, { useContext, useEffect, useState } from "react";
import {
    Text,
    Spinner,
    Card,
    CardHeader,
    CardFooter,
    Button,
    Field,
    Input,
    Combobox,
    Option,
    Divider,
    Dialog,
    DialogBody,
    DialogContent,
    DialogTitle,
    DialogSurface,
    DialogActions,
} from "@fluentui/react-components";
import { Add20Regular, Delete20Regular, Edit20Regular } from "@fluentui/react-icons";
import moment from "moment";
import { ApiContext } from "../providers/ApiProvider";
import { IDefaultHoursAssignment, IProjectResults, IUser } from "../types/types";

interface IDefaultHoursAdminViewProps {
    token: string;
}

interface IAssignmentFormData {
    project_id: string;
    hours_per_day: number;
    date_from: string; // ISO date string or empty
    date_to: string; // ISO date string or empty
}

interface IUserCardProps {
    token: string;
    user: IUser;
    projects: IProjectResults[];
    onAssignmentsChanged?: (assignments: IDefaultHoursAssignment[]) => void;
}

function DefaultHoursUserCard({ token, user, projects, onAssignmentsChanged }: IUserCardProps) {
    const { getDefaultHoursConfigForUser, createDefaultHoursAssignment, updateDefaultHoursAssignment, deleteDefaultHoursAssignment } =
        useContext(ApiContext);

    const [assignments, setAssignments] = useState<IDefaultHoursAssignment[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const [formData, setFormData] = useState<IAssignmentFormData>({
        project_id: "",
        hours_per_day: 7.5,
        date_from: "",
        date_to: "",
    });

    const userIdentifier = user.id || user.oid;

    useEffect(() => {
        if (!userIdentifier) return;
        loadAssignments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdentifier, token]);

    const loadAssignments = async () => {
        if (!userIdentifier) return;
        try {
            setIsLoading(true);
            const list = await getDefaultHoursConfigForUser(token, userIdentifier);
            setAssignments(list);
            if (onAssignmentsChanged) {
                onAssignmentsChanged(list);
            }
        } catch (error) {
            console.error("Error loading default hours assignments for user:", user, error);
            setAssignments([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (assignment: IDefaultHoursAssignment) => {
        setEditingId(assignment.id || null);
        setValidationError(null);
        setFormData({
            project_id: assignment.project_id,
            hours_per_day: assignment.default_hours,
            date_from: assignment.date_range?.from ? moment(assignment.date_range.from).format("YYYY-MM-DD") : "",
            date_to: assignment.date_range?.to ? moment(assignment.date_range.to).format("YYYY-MM-DD") : "",
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setValidationError(null);
        setFormData({
            project_id: "",
            hours_per_day: 7.5,
            date_from: "",
            date_to: "",
        });
    };

    const handleSave = async () => {
        if (!userIdentifier || !formData.project_id || formData.hours_per_day <= 0) {
            return;
        }

        try {
            setValidationError(null);
            setIsLoading(true);

            const todayStart = moment().utc(true).startOf("day").valueOf();

            const effectiveDateFrom = !editingId && !formData.date_from ? moment().format("YYYY-MM-DD") : formData.date_from;

            const newFrom =
                effectiveDateFrom && effectiveDateFrom !== "" ? moment(effectiveDateFrom).utc(true).startOf("day").valueOf() : null;
            const newTo = formData.date_to && formData.date_to !== "" ? moment(formData.date_to).utc(true).endOf("day").valueOf() : null;

            if (newFrom !== null && newFrom < todayStart) {
                setValidationError("Start date cannot be in the past. Please select today or a future date.");
                setIsLoading(false);
                return;
            }

            const assignment: IDefaultHoursAssignment = {
                id: editingId || undefined,
                user_oid: userIdentifier,
                project_id: formData.project_id,
                default_hours: formData.hours_per_day,
                date_range: {
                    from: newFrom,
                    to: newTo,
                },
            };

            // Only one default-hours assignment is allowed per user.
            // When creating a new one, remove any existing assignments first.
            if (!editingId && assignments.length > 0) {
                await Promise.all(
                    assignments
                        .filter((a) => a.id)
                        .map((a) => deleteDefaultHoursAssignment(token, a.id as string)),
                );
            }

            if (editingId) {
                await updateDefaultHoursAssignment(token, assignment, editingId);
            } else {
                await createDefaultHoursAssignment(token, assignment);
            }

            await loadAssignments();
            handleCancelEdit();
        } catch (error) {
            console.error("Error saving default hours assignment:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            setIsLoading(true);
            await deleteDefaultHoursAssignment(token, id);
            await loadAssignments();
        } catch (error) {
            console.error("Error deleting default hours assignment:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getProjectName = (projectId: string): string => {
        const project = projects.find((p) => p.id === projectId);
        return project ? `${project.company_name} - ${project.project_name}` : projectId;
    };

    return (
        <Card className="w-full">
            <CardHeader
                header={
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
                        <div>
                            <Text weight="semibold">{user.full_name}</Text>
                            <div className="text-xs text-(--colorNeutralForeground3) break-all">{user.email}</div>
                        </div>
                        <div className="flex gap-2 text-xs text-(--colorNeutralForeground3)">
                            {user.is_admin && <span className="px-2 py-0.5 rounded-full bg-(--colorPaletteRedBackground2)">Admin</span>}
                            {user.is_project_manager && (
                                <span className="px-2 py-0.5 rounded-full bg-(--colorPaletteBlueBackground2)">Project manager</span>
                            )}
                            {user.disabled && <span className="px-2 py-0.5 rounded-full bg-(--colorPaletteGreyBackground2)">Disabled</span>}
                        </div>
                    </div>
                }
            />
            <Divider />
            <div className="mt-3 space-y-3">
                <div>
                    <Text size={300} weight="semibold">
                        Default hours assignments
                    </Text>
                    {isLoading ? (
                        <div className="flex justify-center py-3">
                            <Spinner size="small" />
                        </div>
                    ) : assignments.length === 0 ? (
                        <Text size={200} className="text-(--colorNeutralForeground3)">
                            No assignments configured for this user.
                        </Text>
                    ) : (
                        <div className="mt-2 space-y-2">
                            {assignments.map((assignment) => (
                                <div key={assignment.id} className="flex items-start justify-between gap-3 p-3 border rounded-lg">
                                    {editingId === assignment.id ? (
                                        <div className="flex-1 space-y-2">
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <Field label="Project" className="flex-1">
                                                    <Combobox
                                                        value={getProjectName(formData.project_id)}
                                                        onOptionSelect={(_, data) => {
                                                            if (data.optionValue) {
                                                                setFormData({
                                                                    ...formData,
                                                                    project_id: data.optionValue,
                                                                });
                                                            }
                                                        }}
                                                        placeholder="Select project (type to filter)..."
                                                        positioning={{ position: "below", align: "start" }}
                                                        listbox={{ style: { maxHeight: "220px", overflowY: "auto" } }}
                                                    >
                                                        {projects.map((project) => (
                                                            <Option
                                                                key={project.id}
                                                                value={project.id}
                                                                text={`${project.company_name} - ${project.project_name}`}
                                                            >
                                                                {project.company_name} - {project.project_name}
                                                            </Option>
                                                        ))}
                                                    </Combobox>
                                                </Field>
                                                <Field label="Hours/Day" style={{ width: "120px" }}>
                                                    <Input
                                                        type="number"
                                                        step="0.5"
                                                        min="0"
                                                        max="24"
                                                        value={formData.hours_per_day.toString()}
                                                        onChange={(e) =>
                                                            setFormData({
                                                                ...formData,
                                                                hours_per_day: parseFloat(e.target.value) || 0,
                                                            })
                                                        }
                                                    />
                                                </Field>
                                            </div>
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <Field label="From Date" className="flex-1">
                                                    <Input
                                                        type="date"
                                                        value={formData.date_from}
                                                        onChange={(e) => {
                                                            const nextFrom = e.target.value;
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                date_from: nextFrom,
                                                                date_to: prev.date_to && prev.date_to < nextFrom ? "" : prev.date_to,
                                                            }));
                                                        }}
                                                    />
                                                </Field>
                                                <Field label="To Date" className="flex-1">
                                                    <Input
                                                        type="date"
                                                        value={formData.date_to}
                                                        min={formData.date_from || undefined}
                                                        onChange={(e) => setFormData({ ...formData, date_to: e.target.value })}
                                                    />
                                                </Field>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button appearance="primary" onClick={handleSave} disabled={isLoading}>
                                                    Save
                                                </Button>
                                                <Button appearance="secondary" onClick={handleCancelEdit}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex-1">
                                                <Text weight="semibold">{getProjectName(assignment.project_id)}</Text>
                                                <div className="text-sm text-(--colorNeutralForeground3)">
                                                    <Text>
                                                        {assignment.default_hours} hours/day
                                                        {assignment.date_range?.from || assignment.date_range?.to
                                                            ? ` • ${
                                                                  assignment.date_range?.from
                                                                      ? moment(assignment.date_range.from).format("MMM D, YYYY")
                                                                      : "No start"
                                                              } - ${
                                                                  assignment.date_range?.to
                                                                      ? moment(assignment.date_range.to).format("MMM D, YYYY")
                                                                      : "No end"
                                                              }`
                                                            : " • No date range"}
                                                    </Text>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1 items-end">
                                                <div className="flex gap-1">
                                                    <Button
                                                        appearance="subtle"
                                                        icon={<Edit20Regular />}
                                                        onClick={() => handleEdit(assignment)}
                                                        aria-label="Edit"
                                                    />
                                                    <Button
                                                        appearance={pendingDeleteId === assignment.id ? "primary" : "subtle"}
                                                        icon={<Delete20Regular />}
                                                        onClick={() => {
                                                            if (!assignment.id) return;
                                                            if (pendingDeleteId === assignment.id) {
                                                                handleDelete(assignment.id);
                                                                setPendingDeleteId(null);
                                                            } else {
                                                                setPendingDeleteId(assignment.id);
                                                                setTimeout(() => {
                                                                    setPendingDeleteId((current) =>
                                                                        current === assignment.id ? null : current,
                                                                    );
                                                                }, 4000);
                                                            }
                                                        }}
                                                        aria-label={
                                                            pendingDeleteId === assignment.id
                                                                ? "Click again to confirm delete"
                                                                : "Delete"
                                                        }
                                                    />
                                                </div>
                                                {pendingDeleteId === assignment.id && (
                                                    <Text size={200} className="text-red-600">
                                                        Click delete again to remove this assignment.
                                                    </Text>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add New Assignment (only when user has no assignment yet, and after assignments have loaded) */}
                {!editingId && assignments.length === 0 && !isLoading && (
                    <div className="border rounded-lg p-3 space-y-2 bg-(--colorNeutralBackground2)">
                        <Text size={300} weight="semibold">
                            Add new assignment
                        </Text>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Field label="Project" className="flex-1" required>
                                <Combobox
                                    value={getProjectName(formData.project_id)}
                                    onOptionSelect={(_, data) => {
                                        if (data.optionValue) {
                                            setFormData({ ...formData, project_id: data.optionValue });
                                        }
                                    }}
                                    placeholder="Select project (type to filter)..."
                                    positioning={{ position: "below", align: "start" }}
                                    listbox={{ style: { maxHeight: "220px", overflowY: "auto" } }}
                                >
                                    {projects.map((project) => (
                                        <Option
                                            key={project.id}
                                            value={project.id}
                                            text={`${project.company_name} - ${project.project_name}`}
                                        >
                                            {project.company_name} - {project.project_name}
                                        </Option>
                                    ))}
                                </Combobox>
                            </Field>
                            <Field label="Hours/Day" style={{ width: "120px" }} required>
                                <Input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    max="24"
                                    value={formData.hours_per_day.toString()}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            hours_per_day: parseFloat(e.target.value) || 0,
                                        })
                                    }
                                />
                            </Field>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Field label="From Date (optional)" className="flex-1">
                                <Input
                                    type="date"
                                    value={formData.date_from}
                                    min={moment().format("YYYY-MM-DD")}
                                    onChange={(e) => {
                                        const nextFrom = e.target.value;
                                        setValidationError(null);
                                        setFormData((prev) => ({
                                            ...prev,
                                            date_from: nextFrom,
                                            date_to: prev.date_to && prev.date_to < nextFrom ? "" : prev.date_to,
                                        }));
                                    }}
                                />
                            </Field>
                            <Field label="To Date (optional)" className="flex-1">
                                <Input
                                    type="date"
                                    value={formData.date_to}
                                    min={formData.date_from || undefined}
                                    onChange={(e) => {
                                        setValidationError(null);
                                        setFormData({ ...formData, date_to: e.target.value });
                                    }}
                                />
                            </Field>
                        </div>
                        <div className="flex flex-col gap-2">
                            {validationError && (
                                <Text size={200} className="text-red-600 whitespace-pre-line">
                                    {validationError}
                                </Text>
                            )}
                            <Button
                                appearance="primary"
                                icon={<Add20Regular />}
                                onClick={handleSave}
                                disabled={!formData.project_id || formData.hours_per_day <= 0 || isLoading}
                            >
                                Add assignment
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            <CardFooter />
        </Card>
    );
}

export default function DefaultHoursAdminView({ token }: IDefaultHoursAdminViewProps) {
    const { getUsers, fetchProjects, getDefaultHoursConfigForUser } = useContext(ApiContext);
    const [users, setUsers] = useState<IUser[]>([]);
    const [projects, setProjects] = useState<IProjectResults[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
    const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(false);
    const [isLoadingAssignments, setIsLoadingAssignments] = useState<boolean>(false);
    const [assignmentsByUser, setAssignmentsByUser] = useState<Record<string, IDefaultHoursAssignment[]>>({});
    const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!token) return;
        loadUsers();
        loadProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (!token) return;
        if (users.length === 0) return;
        loadAssignmentsForUsers(users);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, users]);

    const loadUsers = async () => {
        try {
            setIsLoadingUsers(true);
            const list = await getUsers(token);
            list.sort((a, b) => {
                const nameCompare = a.full_name.localeCompare(b.full_name, undefined, { sensitivity: "base" });
                if (nameCompare !== 0) return nameCompare;
                return a.email.localeCompare(b.email, undefined, { sensitivity: "base" });
            });
            setUsers(list);
        } catch (error) {
            console.error("Error loading users for default hours admin view:", error);
            setUsers([]);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const loadProjects = async () => {
        try {
            setIsLoadingProjects(true);
            const list = await fetchProjects(token);
            list.sort((a, b) => {
                const companyCompare = a.company_name.localeCompare(b.company_name, undefined, { sensitivity: "base" });
                if (companyCompare !== 0) return companyCompare;
                return a.project_name.localeCompare(b.project_name, undefined, { sensitivity: "base" });
            });
            setProjects(list);
        } catch (error) {
            console.error("Error loading projects for default hours admin view:", error);
            setProjects([]);
        } finally {
            setIsLoadingProjects(false);
        }
    };

    const loadAssignmentsForUsers = async (userList: IUser[]) => {
        try {
            setIsLoadingAssignments(true);
            const map: Record<string, IDefaultHoursAssignment[]> = {};
            const results = await Promise.all(
                userList.map(async (user) => {
                    const id = user.id || user.oid;
                    if (!id) return null;
                    try {
                        const list = await getDefaultHoursConfigForUser(token, id);
                        return { userOid: user.oid, assignments: list };
                    } catch (error) {
                        console.error("Error loading default hours assignments for user:", user, error);
                        return { userOid: user.oid, assignments: [] as IDefaultHoursAssignment[] };
                    }
                }),
            );

            results.forEach((res) => {
                if (!res) return;
                map[res.userOid] = res.assignments;
            });

            Object.keys(map).forEach((key) => {
                map[key].sort((a, b) => {
                    const aFrom = a.date_range?.from ?? 0;
                    const bFrom = b.date_range?.from ?? 0;
                    return aFrom - bFrom;
                });
            });
            setAssignmentsByUser(map);
        } catch (error) {
            console.error("Error loading default hours configuration:", error);
            setAssignmentsByUser({});
        } finally {
            setIsLoadingAssignments(false);
        }
    };

    const isInitialLoading = isLoadingUsers || isLoadingProjects || isLoadingAssignments;

    const getProjectName = (projectId: string): string => {
        const project = projects.find((p) => p.id === projectId);
        return project ? `${project.company_name} - ${project.project_name}` : projectId;
    };

    const getCurrentAssignmentForUser = (user: IUser): IDefaultHoursAssignment | undefined => {
        const list = assignmentsByUser[user.oid] || [];
        if (list.length === 0) return undefined;

        const today = moment().utc(true).startOf("day").valueOf();

        const active = list.find((a) => {
            const from = a.date_range?.from ?? Number.MIN_SAFE_INTEGER;
            const to = a.date_range?.to ?? Number.MAX_SAFE_INTEGER;
            return from <= today && today <= to;
        });
        if (active) return active;

        return list.reduce(
            (latest, a) => {
                if (!latest) return a;
                const latestFrom = latest.date_range?.from ?? 0;
                const aFrom = a.date_range?.from ?? 0;
                return aFrom > latestFrom ? a : latest;
            },
            list[0] as IDefaultHoursAssignment | undefined,
        );
    };

    const handleOpenUserDialog = (user: IUser) => {
        setSelectedUser(user);
        setIsDialogOpen(true);
    };

    const handleCloseUserDialog = () => {
        setIsDialogOpen(false);
        setSelectedUser(null);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <Text size={500} weight="semibold">
                    Default hours configuration (all users)
                </Text>
                <Text size={300} className="text-(--colorNeutralForeground2)">
                    View and manage default hours for all users on a single screen. Use this view to configure each user&apos;s default
                    project and hours per day.
                </Text>
            </div>

            {isInitialLoading && (
                <div className="flex items-center gap-2 py-4">
                    <Spinner size="small" />
                    <Text size={300}>Loading users and projects…</Text>
                </div>
            )}

            {!isInitialLoading && users.length === 0 && (
                <Text size={300} className="text-(--colorNeutralForeground3)">
                    No users found.
                </Text>
            )}

            {!isInitialLoading && users.length > 0 && projects.length === 0 && (
                <Text size={300} className="text-(--colorNeutralForeground3)">
                    No projects found. Create projects first to configure default hours.
                </Text>
            )}

            {!isInitialLoading && users.length > 0 && projects.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,2.5fr)_minmax(0,2.5fr)_auto] gap-2 px-3 py-2 bg-(--colorNeutralBackground2) text-xs sm:text-sm font-semibold">
                        <div>User</div>
                        <div>Email</div>
                        <div>Current default project / hours</div>
                        <div className="text-right pr-1">Configure</div>
                    </div>
                    <div className="divide-y">
                        {users.map((user) => {
                            const currentAssignment = getCurrentAssignmentForUser(user);
                            const hasAssignment = !!currentAssignment;
                            return (
                                <div
                                    key={user.id || user.oid}
                                    className="grid grid-cols-[minmax(0,2fr)_minmax(0,2.5fr)_minmax(0,2.5fr)_auto] gap-2 px-3 py-2 items-center text-xs sm:text-sm"
                                >
                                    <div className="truncate">
                                        <span className="font-medium">{user.full_name}</span>
                                        <span className="ml-2 text-(--colorNeutralForeground3)">
                                            {user.is_admin ? "(Admin)" : user.is_project_manager ? "(PM)" : ""}
                                        </span>
                                    </div>
                                    <div className="truncate text-(--colorNeutralForeground2)">{user.email}</div>
                                    <div className="flex flex-col text-(--colorNeutralForeground2)">
                                        {hasAssignment && currentAssignment ? (
                                            <>
                                                <div className="truncate">
                                                    <span className="font-medium">{getProjectName(currentAssignment.project_id)}</span>
                                                    <span className="ml-1">
                                                        • {currentAssignment.default_hours}
                                                        <span className="hidden sm:inline"> h/day</span>
                                                    </span>
                                                </div>
                                                {(currentAssignment.date_range?.from || currentAssignment.date_range?.to) && (
                                                    <div className="text-[0.75rem] sm:text-xs text-(--colorNeutralForeground3)">
                                                        {currentAssignment.date_range?.from
                                                            ? moment(currentAssignment.date_range.from).format("DD/MM/YYYY")
                                                            : "No start"}
                                                        {" – "}
                                                        {currentAssignment.date_range?.to
                                                            ? moment(currentAssignment.date_range.to).format("DD/MM/YYYY")
                                                            : "No end"}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-(--colorNeutralForeground3)">Not configured</span>
                                        )}
                                    </div>
                                    <div className="flex justify-end">
                                        <Button
                                            size="small"
                                            appearance="primary"
                                            onClick={() => handleOpenUserDialog(user)}
                                            className="whitespace-nowrap"
                                        >
                                            Configure
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {selectedUser && (
                <Dialog
                    open={isDialogOpen}
                    onOpenChange={(_, data) => {
                        if (!data.open) {
                            handleCloseUserDialog();
                        }
                    }}
                >
                    <DialogSurface style={{ minWidth: "720px", maxWidth: "960px" }}>
                        <DialogBody>
                            <DialogTitle>
                                <div className="flex flex-col gap-1">
                                    <Text size={500} weight="semibold">
                                        Configure default hours
                                    </Text>
                                    <Text size={300} className="text-(--colorNeutralForeground2)">
                                        {selectedUser.full_name} ({selectedUser.email})
                                    </Text>
                                </div>
                            </DialogTitle>
                            <DialogContent>
                                <DefaultHoursUserCard
                                    token={token}
                                    user={selectedUser}
                                    projects={projects}
                                    onAssignmentsChanged={(updated) => {
                                        setAssignmentsByUser((prev) => ({
                                            ...prev,
                                            [selectedUser.oid]: updated,
                                        }));
                                    }}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button appearance="secondary" onClick={handleCloseUserDialog}>
                                    Close
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            )}
        </div>
    );
}
